import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Col } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { SERVER_IP } from 'assets/Config';
import HighlightComponent from 'components/HighlightComponent';
import { resetApiStatus } from 'redux/reducers/globals/globalActions';
import { formQueryStringFromObject, generatePagination } from 'helpers';
import { API_STATUS } from 'constants/app-constants';
import { getDateFormat } from 'services/Utils';
import { useI18n } from 'hooks/useI18n';
import FoodListPresentational from './foods-list-presentational';

const initialPageSize = 10;
const initialPageSizeOptions = [10, 15, 20];

const FoodListFunctional = React.memo(() => {
	const foodRedux = useSelector((state) => state?.foodRedux);
	const globalRedux = useSelector((state) => state.globalRedux);
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const dispatch = useDispatch();
	const { t } = useI18n();

	const [foodAddModal, setFoodAddModal] = useState(false);
	const [searchKey, setSearchKey] = useState('');
	const [tableData, setTableData] = useState(foodRedux?.foods);
	const [editFood, setEditFood] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(initialPageSize);

	const getFoods = useCallback(() => {
		const string = formQueryStringFromObject({
			branchId: globalRedux?.selectedBranch?.id,
		});
		let url = `${SERVER_IP}api/food?${string}`;
		dispatch(getApi('GET_FOODS', url));
	}, [dispatch, globalRedux?.selectedBranch?.id]);

	useEffect(() => {
		getFoods();
	}, [getFoods]);

	useEffect(() => {
		generatePagination(tableData);
	}, [tableData]);

	useEffect(() => {
		let doIt = false;
		if (globalRedux.apiStatus.DELETE_FOOD === 'SUCCESS') {
			dispatch(resetApiStatus('DELETE_FOOD'));
			setSelectedRowKeys([]);
			doIt = true;
		}
		if (doIt) {
			getFoods();
		}
	}, [globalRedux.apiStatus.DELETE_FOOD, dispatch, getFoods]);

	useEffect(() => {
		setTableData(foodRedux?.foods);
	}, [foodRedux?.foods]);

	const filteredData = useMemo(() => {
		if (searchKey === '') {
			return tableData;
		}
		return tableData.filter((food) => {
			return (food?.name || '')?.toLowerCase().includes(searchKey.toLowerCase());
		});
	}, [tableData, searchKey]);

	const handleDrawer = (rowData) => {
		setEditFood(rowData);
		setFoodAddModal(true);
	};

	const handleAddFood = () => {
		setEditFood(null);
		setFoodAddModal(true);
	};

	const column = [
		{
			title: '#',
			dataIndex: 'foodNumber',
			key: 'foodNumber',
			// fixed: 'left',
			width: '5%',
			render: (value, record, index) => <div>{index + 1}</div>,
		},
		{
			title: t('foods.columns.food_name'),
			dataIndex: 'name',
			key: 'name',
			sorter: (a, b) => a?.name?.localeCompare(b?.name),
			// fixed: 'left',
			render: (value) => (
				<div style={{ fontWeight: 'bold' }}>
					<HighlightComponent highlightClassName="highlightClass" searchWords={[searchKey]} autoEscape={true} textToHighlight={value} />
				</div>
			),
		},
		{
			title: t('foods.columns.created_date'),
			dataIndex: 'created_at',
			align: 'left',
			width: '25%',
			render: (value) => getDateFormat(value),
		},
		{
			title: t('foods.columns.action'),
			align: 'center',
			width: '10%',
			dataIndex: 'name',
			render: (value, row, index) => {
				return (
					<Row justify="center">
						<Col className="edit_icon" onClick={() => handleDrawer(row)}>
							<EditOutlined />
						</Col>
					</Row>
				);
			},
		},
	];

	const handleTableChange = (currentPage, pageSize) => {
		setCurrentPage(currentPage);
		setPageSize(pageSize);
	};

	const getStartingValue = () => {
		if (currentPage === 1) return 1;
		else {
			return (currentPage - 1) * pageSize + 1;
		}
	};

	const getEndingValue = () => {
		if (currentPage === 1) return tableData.length < pageSize ? tableData.length : pageSize;
		else {
			let end = currentPage * pageSize;
			return end > tableData.length ? tableData.length : end;
		}
	};

	const tableLoading = useMemo(() => globalRedux.apiStatus.GET_FOODS === API_STATUS.PENDING, [globalRedux.apiStatus]);

	const onSelectChange = (selectedRowKeys) => {
		console.log('🚀 ~ onSelectChange ~ selectedRowKeys:', selectedRowKeys);
		setSelectedRowKeys(selectedRowKeys);
	};

	const rowSelection = {
		selectedRowKeys,
		onChange: onSelectChange,
		// fixed: true,
	};

	const handleClose = useCallback(() => {
		setFoodAddModal(false);
		setEditFood(null);
	}, [setFoodAddModal, setEditFood]);

	return (
		<FoodListPresentational
			{...{
				filteredData,
				column,
				tableLoading,
				rowSelection,
				selectedRowKeys,
				handleAddFood,
				currentPage,
				pageSize,
				initialPageSizeOptions,
				initialPageSize,
				handleTableChange,
				setSearchKey,
				getStartingValue,
				getEndingValue,
				foodAddModal,
				setFoodAddModal,
				refreshList: getFoods,
				editFood,
				handleClose,
			}}
		/>
	);
});

export default FoodListFunctional;
